import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../config/api';

const ACTION_LABEL = {
  CREATED: 'Created',
  RAISED: 'Raised',
  STATUS_CHANGED: 'Status',
  DELEGATED: 'Delegated',
  UNASSIGNED: 'Delegation',
  ASSIGNED: 'Assigned',
  PROGRESS_CHANGED: 'Progress',
  TITLE_UPDATED: 'Title',
  DESCRIPTION_UPDATED: 'Description',
  ESTIMATE_UPDATED: 'Estimate',
  URGENT_SET: 'Urgent',
  URGENT_CLEARED: 'Urgent',
  MAPPED_TO_WORKFLOW: 'Linked',
  UNMAPPED_FROM_WORKFLOW: 'Unlinked',
  TASK_LINKED: 'Task',
  TASK_UNLINKED: 'Task',
  POST_COMPLETION_UPDATED: 'Follow-up',
  TRACK_UPDATED: 'Tracker',
  TRACK_CLEARED: 'Tracker',
  TYPE_CHANGED: 'Type',
  UNDO_COMPLETE: 'Reopened',
  DELETED: 'Deleted',
};

const formatHistoryWhen = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export default function WorkItemHistoryPanel({
  entityLabel = 'item',
  endpoint,
  fallback,
  reloadToken,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!endpoint) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}${endpoint}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) {
          setItems(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err) {
        if (!cancelled) {
          setItems([]);
          setError(typeof err?.response?.data === 'string'
            ? err.response.data
            : (err?.response?.data?.error || err?.message || 'Could not load history'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [endpoint, reloadToken]);

  const hasCreatedEvent = items.some((item) => {
    const action = String(item?.action || '').toUpperCase();
    return action === 'CREATED' || action === 'RAISED';
  });
  const rows = [
    ...items,
    ...(fallback && !hasCreatedEvent ? [fallback] : []),
  ];

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          History
        </h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Status changes, assignment, and other updates on this {entityLabel}.
        </p>
      </div>
      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading history…</p>
      ) : error && rows.length === 0 ? (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No recorded activity yet. New changes will show up here.
        </p>
      ) : (
        <ol className="relative space-y-0 border-l border-gray-200 dark:border-gray-700 ml-2">
          {rows.map((item, index) => {
            const action = String(item.action || '').toUpperCase();
            return (
              <li key={item.id || `${action}-${item.createdAt}-${index}`} className="relative pl-4 py-2.5">
                <span className="absolute -left-[5px] top-4 h-2.5 w-2.5 rounded-full bg-primary-500 ring-2 ring-white dark:ring-gray-800" />
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-primary-600 dark:text-primary-300">
                    {ACTION_LABEL[action] || action || 'Update'}
                  </span>
                  <span className="text-[11px] text-gray-400 dark:text-gray-500">
                    {formatHistoryWhen(item.createdAt)}
                  </span>
                </div>
                <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-100">
                  {item.summary || ACTION_LABEL[action] || 'Updated'}
                </p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  {item.actorName || 'Someone'}
                </p>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
